import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Megaphone,
  BookOpen,
  NotebookPen,
  Award,
} from 'lucide-react';
import { PlanoAulaSemanal } from '../../components/pedagogico/PlanoAulaSemanal';
import { AlunosEmAtencaoCard } from '../../components/pedagogico/AlunosEmAtencaoCard';
import { NotasProfessor } from '../../components/dashboard/DashboardParts';
import { toMin, cleanName } from './professorUtils';
import { TimelineGrupos } from './TimelineGrupos';
import { DesktopMonthCalendar } from './DesktopMonthCalendar';
import { DesktopAnalyticsCharts } from './DesktopAnalyticsCharts';
import { DesktopProximasAulas } from './DesktopProximasAulas';
import { useProfessorDashboardAnalytics } from './useProfessorDashboardAnalytics';
import { useProfessorGradeSemanal } from './useProfessorGradeSemanal';
import type { HorarioAula } from '../../types/models';
import type { PlanoAula } from '../../api/hooks/usePlanoAula';
import './professor-dashboard-desktop.css';

export type ProfessorHeroState =
  | 'PREPARACAO'
  | 'AULA_AGORA'
  | 'CHAMADA_FEITA'
  | 'PROXIMA'
  | 'PENDENCIAS'
  | 'FIM_DIA'
  | 'SEM_AULAS'
  | 'INTERVALO';

type ChamadaDia = {
  id: string;
  horarioAulaId?: string;
  status: string;
  totalPresentes?: number;
  totalAusentes?: number;
  totalAtrasados?: number;
};

export interface ProfessorDashboardDesktopProps {
  user: { id: string; nome?: string };
  loadingHorarios: boolean;
  loadingChamadas: boolean;
  heroState: ProfessorHeroState;
  heroColors: Record<ProfessorHeroState, { bg: string; accent: string }>;
  horarioAtual: HorarioAula | null;
  intervaloAtual: HorarioAula | null;
  proximaAula: HorarioAula | undefined;
  aulasHoje: HorarioAula[];
  slotsHoje: HorarioAula[];
  aulaProgress: { pct: number; restSec: number };
  barColor: string;
  chamadasDia: ChamadaDia[];
  statusMap: Record<string, string>;
  perdidas: HorarioAula[];
  futuras: HorarioAula[];
  chamadasFeitas: number;
  totalAulas: number;
  pctDia: number;
  agoraMin: number;
  agoraSec: number;
  hoje: string;
  planosSemana: PlanoAula[];
  loadingPlanos: boolean;
  semanaAtual: number;
  setSemanaAtual: React.Dispatch<React.SetStateAction<number>>;
  anoLetivoAtual: number;
  alunosAtencaoCount?: number;
}

function buildTimelineGrupos(slotsHoje: HorarioAula[]) {
  type TurnoGroup = {
    nome: string;
    inicio: string;
    fim: string;
    color: string;
    bg: string;
    aulas: HorarioAula[];
    _turnoId?: string;
  };
  const grupos: TurnoGroup[] = [];
  slotsHoje.forEach((h) => {
    const turnoReal = (h as HorarioAula & { turma?: { turnoOperacional?: { id: string; nome: string } } })
      .turma?.turnoOperacional;
    const turnoId = turnoReal?.id || 'sem-turno';
    const nomeT = turnoReal?.nome || 'Turno';
    let colorT = '#64748b';
    let bgT = '#f8fafc';
    const nomeLower = nomeT.toLowerCase();
    if (nomeLower.includes('matutino') || nomeLower.includes('manha')) {
      colorT = '#0369a1';
      bgT = '#f0f9ff';
    } else if (nomeLower.includes('vespertino') || nomeLower.includes('tarde')) {
      colorT = '#b45309';
      bgT = '#fffbeb';
    } else if (nomeLower.includes('noturno') || nomeLower.includes('noite')) {
      colorT = '#7c3aed';
      bgT = '#f5f3ff';
    } else {
      colorT = '#0f766e';
      bgT = '#f0fdfa';
    }
    const last = grupos[grupos.length - 1];
    if (!last || last._turnoId !== turnoId) {
      grupos.push({ nome: nomeT, inicio: '', fim: '', color: colorT, bg: bgT, aulas: [h], _turnoId: turnoId });
    } else {
      last.aulas.push(h);
    }
  });
  grupos.forEach((g) => {
    g.inicio = g.aulas[0]?.horaInicio || '';
    g.fim = g.aulas[g.aulas.length - 1]?.horaFim || '';
  });
  return grupos;
}

function FocoAtualPanel({ props }: { props: ProfessorDashboardDesktopProps }) {
  const { heroState, horarioAtual, intervaloAtual, proximaAula, chamadasDia, chamadasFeitas, totalAulas } = props;

  return (
    <div className="dash-desk-card">
      <div className="dash-desk-card-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background:
                heroState === 'PREPARACAO'
                  ? '#3b82f6'
                  : heroState === 'AULA_AGORA'
                    ? '#10b981'
                    : heroState === 'INTERVALO'
                      ? '#f59e0b'
                      : '#94a3b8',
            }}
          />
          <h3 className="dash-desk-card-title" style={{ color: '#0f172a' }}>
            Foco atual
          </h3>
        </div>
      </div>
      <div style={{ padding: '14px 16px', fontSize: '0.82rem' }}>
        {heroState === 'PREPARACAO' && (
          <>
            <p style={{ margin: '0 0 10px', color: '#1e40af', lineHeight: 1.5 }}>
              <strong>Dica:</strong> Revise o plano da semana e deixe a chamada pronta antes do sinal.
            </p>
            {proximaAula && (
              <p style={{ margin: 0, color: '#64748b' }}>
                Próxima: {proximaAula.materia?.nome} às {proximaAula.horaInicio}
              </p>
            )}
          </>
        )}
        {heroState === 'AULA_AGORA' && horarioAtual && (
          <p style={{ margin: 0, color: '#059669', fontWeight: 600 }}>
            {horarioAtual.materia?.nome} — {horarioAtual.turma?.nome}
          </p>
        )}
        {heroState === 'INTERVALO' && intervaloAtual && (
          <p style={{ margin: 0, color: '#b45309' }}>{intervaloAtual.descricao || 'Intervalo'} em andamento.</p>
        )}
        {heroState === 'CHAMADA_FEITA' && horarioAtual && (
          <p style={{ margin: 0, color: '#166534' }}>Registre o conteúdo no Diário de Classe.</p>
        )}
        {heroState === 'PROXIMA' && proximaAula && (
          <p style={{ margin: 0, color: '#92400e' }}>{proximaAula.turma?.nome} em breve.</p>
        )}
        {heroState === 'FIM_DIA' && (
          <p style={{ margin: 0, color: '#166534' }}>
            {chamadasFeitas}/{totalAulas} chamadas — dia encerrado.
          </p>
        )}
        {(heroState === 'PENDENCIAS' || heroState === 'SEM_AULAS') && (
          <p style={{ margin: 0, color: '#64748b' }}>Use a agenda da semana ou acesse comunicados.</p>
        )}
      </div>
    </div>
  );
}

export function ProfessorDashboardDesktop(props: ProfessorDashboardDesktopProps) {
  const navigate = useNavigate();
  const {
    user,
    heroState,
    horarioAtual,
    intervaloAtual,
    proximaAula,
    aulasHoje,
    slotsHoje,
    statusMap,
    chamadasDia,
    perdidas,
    futuras,
    chamadasFeitas,
    totalAulas,
    pctDia,
    agoraMin,
    hoje,
    planosSemana,
    loadingPlanos,
    semanaAtual,
    setSemanaAtual,
    anoLetivoAtual,
  } = props;

  const firstName = cleanName(user.nome || '').split(' ')[0] || 'Professor';
  // Saudação / data pelo relógio e dia da simulação (não pelo wall clock).
  const horaSim = Math.floor(Math.max(0, agoraMin) / 60) % 24;
  const saudacao = horaSim < 12 ? 'Bom dia' : horaSim < 18 ? 'Boa tarde' : 'Boa noite';
  const dataLabel = useMemo(() => {
    const base =
      hoje && /^\d{4}-\d{2}-\d{2}$/.test(hoje)
        ? new Date(`${hoje}T12:00:00`)
        : new Date();
    return base.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }, [hoje]);

  const grupos = useMemo(() => buildTimelineGrupos(slotsHoje), [slotsHoje]);
  const proximaProxima = proximaAula?.horaInicio ? toMin(proximaAula.horaInicio) - agoraMin <= 20 : false;
  const turnoAtivoIdx = grupos.findIndex((g) =>
    g.aulas.some(
      (h) =>
        horarioAtual?.id === h.id ||
        intervaloAtual?.id === h.id ||
        (proximaProxima && proximaAula?.id === h.id),
    ),
  );
  const idxAtivo = turnoAtivoIdx >= 0 ? turnoAtivoIdx : -1;

  const analytics = useProfessorDashboardAnalytics(user.id);
  const grade = useProfessorGradeSemanal(user.id);

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page">
      <div className="dash-desk-root">
      <div className="dash-desk-top">
        <div className="dash-desk-greeting">
          <h1>
            {saudacao}, {firstName}
          </h1>
          <p>{dataLabel}</p>
        </div>
        <div className="dash-desk-actions">
          <button type="button" className="dash-desk-action-btn primary" onClick={() => navigate('/professor/chamada')}>
            <ClipboardList size={14} /> Chamada
          </button>
          <button type="button" className="dash-desk-action-btn" onClick={() => navigate('/professor/diario')}>
            <NotebookPen size={14} /> Diário
          </button>
          <button type="button" className="dash-desk-action-btn" onClick={() => navigate('/professor/comunicados')}>
            <Megaphone size={14} /> Comunicados
          </button>
        </div>
      </div>

      <div className="dash-desk-shell">
        <div className="dash-desk-main">
          <div className="dash-desk-metrics-sync">
            <div className="dash-desk-kpis-sync" aria-label="Indicadores do dia">
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                  <BookOpen size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{totalAulas}</div>
                  <div className="dash-desk-kpi-lbl">Aulas hoje</div>
                </div>
              </div>
              <div className="dash-desk-kpis-mid">
                <div className="dash-desk-kpi">
                  <div className="dash-desk-kpi-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                    <CheckCircle2 size={15} />
                  </div>
                  <div>
                    <div className="dash-desk-kpi-val">{chamadasFeitas}</div>
                    <div className="dash-desk-kpi-lbl">Chamadas feitas</div>
                  </div>
                </div>
                <div className="dash-desk-kpi">
                  <div
                    className="dash-desk-kpi-icon"
                    style={{
                      background: perdidas.length ? '#fef3c7' : '#f8fafc',
                      color: perdidas.length ? '#d97706' : '#94a3b8',
                    }}
                  >
                    <AlertTriangle size={15} />
                  </div>
                  <div>
                    <div className="dash-desk-kpi-val">{perdidas.length}</div>
                    <div className="dash-desk-kpi-lbl">Fora do Horário</div>
                  </div>
                </div>
              </div>
              <div className="dash-desk-kpi">
                <div className="dash-desk-kpi-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                  <Award size={15} />
                </div>
                <div>
                  <div className="dash-desk-kpi-val">{planosSemana.length}</div>
                  <div className="dash-desk-kpi-lbl">Planos (semana)</div>
                </div>
              </div>
            </div>

            <DesktopAnalyticsCharts
            resumo={analytics.resumo}
            turmasFrequencia={analytics.turmasFrequencia}
            countsSemana={grade.counts}
            isLoading={analytics.isLoading || grade.isLoading}
          />
          </div>

          <div className="dash-desk-calendar-slot">
            <DesktopMonthCalendar
              professorId={user.id}
              statusMap={statusMap}
              onIrChamada={() => navigate('/professor/chamada')}
              hojeIso={hoje}
            />
          </div>

          {totalAulas > 0 && (
            <div className="dash-desk-shell-extra">
              <div className="dash-desk-grid">
                <div className="dash-desk-card">
                  <div className="dash-desk-card-head">
                    <h3 className="dash-desk-card-title">Aulas de hoje</h3>
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                      {totalAulas} aulas · {pctDia}%
                    </span>
                  </div>
                  <div className="dash-desk-card-body">
                    <TimelineGrupos
                      grupos={grupos}
                      idxAtivo={idxAtivo}
                      statusMap={statusMap}
                      horarioAtual={horarioAtual}
                      intervaloAtual={intervaloAtual}
                      proximaAula={proximaAula}
                      agoraMin={agoraMin}
                      chamadasDia={chamadasDia}
                      navigate={navigate}
                      dataSelecionada={hoje}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="dash-desk-sidebar">
          <aside className="dash-desk-aside-stack" aria-label="Resumo lateral">
            <FocoAtualPanel props={props} />
            <NotasProfessor professorId={user.id} />
            <AlunosEmAtencaoCard
              professorId={user.id}
              onClickAluno={(id) => navigate(`/professor/alunos/${id}`)}
            />
          </aside>

          <DesktopProximasAulas
            variant="sidebar"
            slotsByDia={grade.slotsByDia}
            hojeIso={hoje}
            agoraMin={agoraMin}
            statusMap={statusMap}
            isLoading={grade.isLoading}
            totalAulasSemana={grade.totalAulasSemana}
          />

          <div className="dash-desk-aside-plano" id="plano-semanal-section">
            <PlanoAulaSemanal
              variant="sidebar"
              planos={planosSemana}
              professorId={user.id}
              semanaAno={semanaAtual}
              anoLetivo={anoLetivoAtual}
              isLoading={loadingPlanos}
              onNavSemana={(delta) =>
                setSemanaAtual((prev) => Math.max(1, Math.min(53, prev + delta)))
              }
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
